/**
 * constants.ts — the vocabulary this app shares with the e-signing schema.
 *
 * Every string here is one half of a contract with SQL or with the edge renderer. The
 * party labels are the `agreement_party` enum verbatim (the PDF's `:::signature role=X:::`
 * slots key on the same values), the document types are `agreement_doc_type`, and the
 * buckets are the map `agreement_finalize` re-derives server-side. Renaming one side
 * without the other produces a silently empty signature slot, not an error.
 */

import type { LucideIcon } from 'lucide-react';
import { FileText, Handshake, ShieldCheck, Banknote, Wallet, UserCheck } from 'lucide-react';

// ─── Documents ───────────────────────────────────────────────────────────────

export type AgreementDocType =
    | 'HIRE_PURCHASE'
    | 'WITNESS'
    | 'LOAN'
    | 'INSURANCE_LOAN'
    | 'DEPOSIT'
    | 'PARTNER_MANAGEMENT';

export type AgreementDocStatus =
    | 'PENDING_REVIEW'
    | 'IN_SIGNING'
    | 'COMPLETED'
    | 'VOIDED'
    | 'SUPERSEDED';

export type AgreementParty = 'DRIVER' | 'WITNESS' | 'COMPANY_REP' | 'BORROWER' | 'PARTNER';

export type AgreementSignatureStatus =
    | 'PENDING'
    | 'SENT'
    | 'VIEWED'
    | 'SIGNED'
    | 'DECLINED'
    | 'VOIDED';

interface DocTypeMeta {
    label: string;
    /** What the subject id passed to `agreement_create` refers to. */
    subject: 'contract' | 'loan' | 'insurance_loan' | 'deposit';
    icon: LucideIcon;
    /**
     * Never required for contract activation — asked of a SLOT THAT EXISTS, which is the only
     * place this flag is read.
     *
     * A STATIC FLAG CAN STILL ANSWER IT FOR THE TWO CONDITIONAL GATES, BECAUSE THEIR SLOTS ARE
     * CONDITIONAL TOO. `buildBoardSlots` mints one DEPOSIT slot per deposit and one INSURANCE_LOAN
     * slot per insurance loan, so a row of either type on screen IS a contract carrying that
     * thing — and `sign_contract` refuses both from there ([D-B15], [D-B14]). Neither may wear an
     * `Optional` badge: the loan one wore it on every financed contract, and 170 of the 196
     * carrying a loan have no sealed loan agreement at all.
     *
     * NOTHING WEARS THE BADGE ANY MORE, AND THE FLAG STAYS (owner's ruling, 2026-09-16). The
     * standing operational expense advance was the last `true`: it is still no part of this
     * contract's ACTIVATION — `sign_contract` never reads it — but it is now the whole purpose of
     * the advance setup flow, and a document an admin is walked through generating and executing is
     * not one the board may label "Optional" on the row they are doing it from. `AgreementDocumentRow`
     * therefore has no live trigger for the badge; the flag is kept because the QUESTION it answers
     * is still a real one and the next doc type will have to answer it.
     *
     * The MANIFEST's dormancy flag is a different question and its answer is now the same one: its
     * rows are drawn whether or not the contract carries the thing, and neither is dormant on any
     * contract (`setupState.ts`, owner's ruling) — and it prints NOTHING either way. The
     * word "optional" never renders for the deposit or the insurance-loan agreement, on any
     * surface, in any state (owner's ruling); this flag is the badge on `AgreementDocumentRow`,
     * and `false` here is what keeps it off both of them.
     */
    optional: boolean;
    /** Who signs, in order. The company is always last (countersign-last). */
    parties: AgreementParty[];
    description: string;
}

export const DOC_TYPES: Record<AgreementDocType, DocTypeMeta> = {
    /**
     * TWO SEATS, NOT THREE [D-B18]. The witness came off this instrument in template v3: they do
     * not sign the hire purchase agreement, they sign the witness agreement — which is the one
     * whose finalize stamps the WITNESS legacy row the activation gate reads. A third seat here
     * would draw a chip for a signature the server never creates.
     */
    HIRE_PURCHASE: {
        label: 'Hire Purchase Agreement',
        subject: 'contract',
        icon: FileText,
        optional: false,
        parties: ['DRIVER', 'COMPANY_REP'],
        description: 'Driver and Primelane',
    },
    WITNESS: {
        label: 'Witness Agreement',
        subject: 'contract',
        icon: UserCheck,
        optional: false,
        parties: ['WITNESS', 'COMPANY_REP'],
        description: 'Witness and Primelane',
    },
    /**
     * THE TWO ADVANCE INSTRUMENTS ARE NAMED BY WHAT THEY ADVANCE, NOT BY THE TABLE THEY HANG OFF.
     * Both labels are the PUBLISHED TITLES of `agreement_templates` v1 verbatim — the schedules are
     * headed "Insurance Premium Advance" and "Operational Expense Advance", and their bodies say
     * "the Amount advanced" throughout. The subject keys, the doc-type enum and the buckets keep the
     * `loan` spelling because they are the schema; only the words a driver and an admin read moved.
     * A label that still said "Loan Agreement" would name the row differently from the document that
     * opens out of it.
     *
     * ── THE PRECEDENCE REVERSED AT [D-B45], AND `INSURANCE_LOAN`'S LABEL IS NOW A FALLBACK ──────
     * This map used to OUTRANK the template's own title: one doc type, one published title, and the
     * label was the better place to keep it because an EMPTY slot has no template to ask. That
     * stopped being true when a premium the driver pays in full got an instrument of its own. One
     * `doc_type` now carries TWO published titles — `insurance_loan` "Insurance Premium Advance" and
     * `insurance_payment` "Insurance Premium Payment Record" — so a name taken from the type would
     * call a Payment Record an advance on the board, in the send confirmation, in the paper sheet
     * and on the Templates page, where the two families would appear under one name.
     *
     * So the order is now: the DOCUMENT's own `agreement_templates.title` where one exists
     * (`BoardSlot.title`, `groupTemplates`), the INSTRUMENT's title on an empty insurance slot
     * (`instrument.ts`), and this label last. It is unreversed for every other type — where a doc
     * type publishes exactly one title, the two answers are the same string, verified against all
     * 22 catalogue rows — and the entry below stays worded as the ADVANCE, which is what
     * `instrumentFor` falls back to whenever the shape is unknown.
     */
    INSURANCE_LOAN: {
        label: 'Insurance Premium Advance',
        subject: 'insurance_loan',
        icon: ShieldCheck,
        // [D-B14]: this slot exists only where a loan does, and a loan is a second debt the
        // server will not activate the contract without.
        optional: false,
        parties: ['BORROWER', 'COMPANY_REP'],
        description: 'Borrower and Primelane',
    },
    LOAN: {
        label: 'Operational Expense Advance',
        subject: 'loan',
        icon: Banknote,
        // The last `true`, and it went with the advance's own setup flow (owner's ruling,
        // 2026-09-16): the Schedule is executed in the app now, so the row an admin generates it
        // from must not be labelled optional. Activation still never reads it — see `optional`.
        optional: false,
        parties: ['BORROWER', 'COMPANY_REP'],
        description: 'Borrower and Primelane',
    },
    DEPOSIT: {
        label: 'Deposit Agreement',
        subject: 'deposit',
        icon: Wallet,
        optional: false,
        parties: ['DRIVER', 'COMPANY_REP'],
        description: 'Driver and Primelane',
    },
    /**
     * THE VEHICLE SCHEDULE — PRIMELANE'S BARGAIN WITH THE OWNER OF THE CAR [D-B48].
     *
     * ITS SUBJECT IS THE DRIVER CONTRACT, NOT THE VEHICLE AND NOT THE PARTNER, because every
     * figure it states derives from that contract: the weekly payment is the contract's
     * instalment, the expected duration is its term, and the partner's share is what is left of
     * each payment after the contract's management rate. `agreement_create` is called with the
     * contract id exactly as the hire purchase and witness agreements are.
     *
     * THE LABEL IS THE PUBLISHED TITLE OF `partner_vehicle_schedule` v1, and it is a fallback
     * only [D-B45]: a generated document answers for itself through `BoardSlot.title`. Pinning
     * the published words here is what keeps an EMPTY slot — the row an admin clicks Generate on
     * — from being named one thing and arriving as another.
     *
     * NOT OPTIONAL, AND CONDITIONAL IS A DIFFERENT QUESTION. `get_contract_setup_status` requires
     * a sealed schedule on every third-party vehicle and is silent on Primelane's own, so where
     * the slot EXISTS it is required — which is the only state this flag is ever read in
     * (`optional`'s own docblock). `buildBoardSlots` mints it only on a third-party vehicle, so
     * the conditionality lives in the minting rather than in a badge.
     *
     * NO DRIVER SEAT. The driver is not a party to it, is not named on it, and cannot read it —
     * `can_read_agreement_document` takes the row away from them by doc type.
     */
    PARTNER_MANAGEMENT: {
        label: 'Hire Fleet Management Agreement',
        subject: 'contract',
        icon: Handshake,
        optional: false,
        parties: ['PARTNER', 'COMPANY_REP'],
        description: 'Partner and Primelane',
    },
};

/**
 * The statuses that hold an arc's live-document slot.
 *
 * The partial-unique index behind every arc is keyed on `status NOT IN ('VOIDED','SUPERSEDED')`,
 * so this is the client-side reading of the same rule: at most one of these exists per subject
 * at a time, and a surface that shows a voided document as the current one is showing wording
 * nobody is bound by.
 */
const LIVE_DOC_STATUSES = new Set<string>(['PENDING_REVIEW', 'IN_SIGNING', 'COMPLETED']);

export function isLiveDocument(status: string | null | undefined): boolean {
    return !!status && LIVE_DOC_STATUSES.has(status);
}

/**
 * Where an executed PDF lands. `agreement_finalize` re-derives this and rejects a mismatch.
 *
 * THE VEHICLE SCHEDULE REUSES THE CONTRACT BUCKET [D-B48], and that is the server's own choice
 * rather than a shortcut taken here: its executed copy is filed at
 * `generated/<contract_id>/<doc>/final.pdf` under the reserved prefix that keeps the bucket
 * admin-only in storage, and the partner reaches their copy through `my_signed_copy`. No new
 * bucket, no new storage policy.
 *
 * AND IT HAS NO CLIENT CONSUMER LEFT AT ALL SINCE [D-B49], which is stated here so nobody reads it
 * as a live address. Its one caller was the paper-filing sheet, which asked it where to PUT a scan;
 * `authenticated` has lost that write on all five arcs and the sheet is gone. Every read path
 * carries its own bucket on the row it is reading — `View scan` signs `legacy_url`, and a sealed
 * copy is fetched by the document's own `final_bucket`/`final_path` — so nothing here is derived
 * from a doc type any more.
 *
 * IT IS KEPT AS THE ADDRESS OF RECORD, AND AS ONE OF THE FIVE TOTAL MAPS A NEW DOC TYPE HAS TO
 * ANSWER. `agreement_finalize` re-derives exactly this in SQL and rejects a mismatch, so where a
 * client ever needs the bucket again it needs this answer and not another one; and being a total
 * `Record<AgreementDocType, …>` is what makes the compiler name every site a new instrument has to
 * be thought about — which is how this family was added.
 */
export const DOC_BUCKETS: Record<AgreementDocType, string> = {
    HIRE_PURCHASE: 'contract-agreements',
    WITNESS: 'contract-agreements',
    DEPOSIT: 'contract-deposit-agreements',
    LOAN: 'loan-agreements',
    INSURANCE_LOAN: 'insurance-loan-agreements',
    PARTNER_MANAGEMENT: 'contract-agreements',
};

// ─── Whose vehicle it is ─────────────────────────────────────────────────────

/**
 * Primelane's own partner row — the HOUSE fleet, 259 of the 289 vehicles.
 *
 * THE SERVER IS THE TRUTH AND IT DOES NOT USE THIS NUMBER. `partners.is_house` is the column
 * `agreement_create` and `get_contract_setup_status` test, guarded by a partial unique index (at
 * most one house row) and a trigger that refuses a demotion — so the fact travels with the
 * partner record rather than with an id. `AGREEMENT_PARTNER_IS_HOUSE` is what a client that got
 * this wrong would meet.
 *
 * THE MIRROR EXISTS SO A SLOT IS NEVER OFFERED INTO THAT REFUSAL. The board holds the vehicle's
 * `partner_id` already (it is on the contract row it reads); reading `is_house` as well would be
 * a second round trip on every canvas to answer a question the migration settled by marking id 1.
 * Where the two could ever disagree — a house marker moved in SQL — the server refuses and this
 * app surfaces its sentence, which is the ordinary shape of every other client-side precondition
 * on this board.
 */
export const HOUSE_PARTNER_ID = 1;

/**
 * Whether a vehicle's owner is somebody Primelane owes a Vehicle Schedule to.
 *
 * NULL IS NOT A THIRD-PARTY VEHICLE. `vehicles.partner_id` is NOT NULL in the schema, so an
 * absent value here means the board has not read the vehicle yet — a contract with no vehicle at
 * all, or a row still in flight. Minting a schedule slot on that would offer to generate a
 * document about a car nobody has named, which `agreement_create` answers with
 * `AGREEMENT_SUBJECT_INCOMPLETE`.
 */
export function isThirdPartyVehicle(partnerId: number | null | undefined): boolean {
    return partnerId !== null && partnerId !== undefined && partnerId !== HOUSE_PARTNER_ID;
}

export const PARTY_LABELS: Record<string, string> = {
    DRIVER: 'Driver',
    BORROWER: 'Borrower',
    WITNESS: 'Witness',
    COMPANY_REP: 'Primelane',
    PARTNER: 'Partner',
};

/**
 * The deposit arc's reading of the same seats.
 *
 * THE DRIVER SEAT ON A DEPOSIT AGREEMENT IS NOT A DRIVER. It is whoever paid, and on a
 * deposit taken before a vehicle was allocated that person has no driver row and no account
 * at all — which is exactly why `agreement_create` freezes their name onto the seat instead
 * of pointing it at a profile. A chip that said "Driver" would name a record that does not
 * exist; every other seat is spoken about the same way it is everywhere else.
 */
export const DEPOSIT_PARTY_LABELS: Record<string, string> = {
    DRIVER: 'Depositor',
};

/** The bucket every signature PNG lives in, admins' saved signatures included. */
export const SIGNATURE_BUCKET = 'agreement-signatures';

/**
 * The storage URL shape `useSignedUrl` and `openSignedUrl` parse.
 *
 * The bucket is private and admin-read-only, so nothing opens without a signed URL; this
 * builds the address those two helpers sign, rather than each caller re-deriving it.
 */
export function signatureObjectUrl(path: string): string {
    return `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/authenticated/${SIGNATURE_BUCKET}/${path}`;
}

/**
 * Recorded on the signature row as `consent_text_version`. Bump it whenever the
 * attestation wording in the countersign dialog changes — the point of the column is that
 * a signature can be read back against the exact sentence its signer agreed to.
 *
 * ── v2: THE INSTRUMENT SPLIT IN TWO, AND ONLY ONE HALF IS ON SCREEN ─────────────────────────
 * The hire purchase, loan and insurance-loan agreements are now a signed SCHEDULE that
 * incorporates a separately published TERMS document by version and SHA-256. The schedule is
 * what gets hashed, sent and countersigned; the terms are bound by the checksum printed inside
 * it. So the `content_sha256` this dialog shows — the one line an admin is invited to read as
 * "what I am attesting to" — now covers half of the agreement rather than all of it, and the
 * company representative is the ONE party who never sees the other half in their own ceremony:
 * the driver is shown the document, the countersigner is shown a hash.
 *
 * That is a change in what the tick means, not merely in what the document says, which is why
 * this is a bump and not a silent edit. The added clause is an AUTHORITY statement and stops
 * deliberately short of a factual one: it says the signatory may bind the company to the terms
 * the instrument cites, and it does not say the signatory knows those terms were handed over —
 * the schedule's own clause makes that representation, and an admin countersigning weeks later
 * could not honestly swear to it.
 *
 * `any` RATHER THAN `the`, because this one sentence serves all five document types and two of
 * them incorporate nothing: the deposit and witness agreements stayed unified single documents.
 * "The terms it incorporates" would have them attesting to a citation that is not on the page.
 *
 * ── v3: THE CITATION IS A PREFIX, AND THE SENTENCE NOW SAYS SO ──────────────────────────────
 * v2 read "by version and checksum", which describes a schedule that prints the whole hash. The
 * published bodies do not: clause 3.1 of all three schedules cites the Terms by version, form and
 * "SHA-256 checksum beginning <16 characters>", and then states that those 16 characters are the
 * BEGINNING of that checksum and that Primelane records the whole of it and gives it out on
 * request. Clause 3.3 is the sentence this one now mirrors — "in the version and with the checksum
 * identified in clause 3.1" — so the tick attests to the terms the instrument IDENTIFIES rather
 * than to a full checksum the countersigner was never shown.
 *
 * IT IS STILL THE DOCUMENT'S OWN `content_sha256` THAT THE DIALOG PRINTS, and that one is whole.
 * The two hashes are different things: the schedule is hashed in full and countersigned against
 * that hash; the Terms are bound to it by the prefix printed inside the schedule's own text.
 */
export const COUNTERSIGN_CONSENT_VERSION = 'admin-countersign-v3';

/** The sentence that version names. Shown in the dialog, stored by reference. */
export const COUNTERSIGN_ATTESTATION =
    'I confirm I am authorised to bind Jpad Prime Lane Ltd to this agreement, including any ' +
    'terms it incorporates, in the version and with the checksum identified in it, and that the ' +
    'signatures already on it were captured through the Primelane signing flow.';

// ─── Merge pre-flight deep links ─────────────────────────────────────────────

/**
 * Which SETUP AREA an admin goes to, to fill in a merge field the document is missing.
 *
 * Keyed by the dotted prefix `agreement_merge_data` emits. The longest matching prefix wins, so
 * a specific field can override its namespace. A field with no entry — or with an explicit null
 * — gets NO link rather than a wrong one: `document.*` and `company.*` are constants nobody
 * edits, and a link that lands on a screen without the field is worse than no link at all.
 *
 * THE OVERRIDES BELOW ARE WHERE A FIELD LIVES SOMEWHERE ITS NAMESPACE DOES NOT.
 * `contract.deposit_amount` is a contract field resolved from the contract's `contract_deposits`
 * rows — '0.00' where there are none [D-B41], NULL where they exist and sum to zero, the SUM
 * otherwise — so it is fixed on the deposit area while plain `contract.` still means the money
 * clauses;
 * `contract.hire_purchase_signed_date` is the witness agreement's dependency on the hire purchase
 * agreement — since [D-B18] it resolves from the DRIVER's signature (the legacy row first, else the
 * live document's DRIVER seat), which is the very fact the locked witness row states; and
 * `contract.hire_purchase_reference` names that same instrument rather than a money clause, so an
 * admin sent to it lands on the agreements area where the document is, not on the figures.
 *
 * `insurance.` IS A NAMESPACE OF ITS OWN, NOT A SHORTER `insurance_loan.`. The policy period is a
 * fact about the cover; the loan is the optional debt that bought it, and a contract can hold the
 * first without the second. Prefix matching keeps them apart on its own — `insurance_loan.x` does
 * not start with `insurance.` — and both point at the same area regardless, which is why the
 * entries are separate rather than one shortened to cover both.
 *
 * THE SCHEDULE'S DERIVED PAIR NEEDS NO ENTRY, AND THAT IS THE ANSWER RATHER THAN AN OVERSIGHT.
 * `contract.term_weeks` and `contract.expected_completion_date` are not stored anywhere: the merge
 * query amortises the selling price less the deposit over the weekly instalment and dates the last
 * one off the earliest instalment start. Every input to both is edited on the Financials canvas, so
 * the plain `contract.` prefix already sends an admin to the only screen that can move them, and an
 * entry of their own would be a second place for that answer to drift from.
 *
 * THE `loan.` NAMESPACE HAS SOMEWHERE TO GO NOW, AND THAT IS WHY ITS NULL IS GONE. Every field in
 * it is edited on the advance dossier — `loan.purpose`, the rate and the disbursement on the
 * DISBURSEMENT area, the weekly figure and the first repayment date on REPAYMENT — so the sentence
 * this table used to print ("no screen can fill this") stopped being true the moment that flow
 * shipped. The two repayment keys are EXACT entries beating the prefix, which is the mechanism this
 * table has always used for a field that lives somewhere its namespace does not; everything else
 * `loan.` is, or is derived from, a figure on the disbursement area.
 *
 * THE VALUES ARE NOT `SetupArea` VALUES, AND NOTHING HERE PRETENDS THEY ARE. This table's type has
 * always been `string | null` — `stepForMergeField` hands a KEY back, and its callers resolve that
 * key against whichever vocabulary they belong to (`resolveArea` in setup, `resolveAdvanceArea` in
 * the dossier, `labelForArea` for the sentence either way). A `loan.` key cannot reach the setup
 * canvas to be mis-resolved there: setup mints no LOAN slot, so no LOAN document's pre-flight is
 * ever drawn on it.
 *
 * THE VEHICLE SCHEDULE'S TWO NAMESPACES GO TO TWO DIFFERENT PLACES [D-B48, D-B59]. `partner.` is
 * the partner RECORD — the name, the registered address and the login's phone — which is edited on
 * `/dashboard/partners/:id` and nowhere in setup: `steps.ts` is a closed union of six areas and
 * none of them is the partner, so it takes the deliberate null. `schedule.` is the Cash price and
 * everything worked out from it and from the contract's money: the cash price, the selling price,
 * the weekly instalment and the management rate, every one of them edited on Financials. The Cash
 * price is `contracts.cash_price` copied onto the referent at generation (the key is still
 * `schedule.all_in_cost`, a name kept for history), so the whole prefix routes there.
 *
 * `contract.management_rate` NEEDS NO ENTRY OF ITS OWN, and that is the answer rather than an
 * omission — `contract.term_weeks`' reasoning above, applied again. The plain `contract.` prefix
 * already routes to Financials, and the management rate really IS edited there
 * (`setup/data/financials.ts`, `areas/FinancialsArea.tsx`), so an override would be a second place
 * for one answer to drift from. `vehicle.chassis` rides `['vehicle.', 'vehicle']` for the same
 * reason: it is `vehicles.vin`, edited on the vehicle.
 */
/*
 * IT IS EXPORTED SO A TEST CAN ITERATE THE REAL VOCABULARY RATHER THAN A COPY OF IT. Every
 * "Resolve in …" control a pre-flight draws routes through `stepForMergeField`, and the dossiers'
 * own resolvers are asserted TOTAL over its answers — an assertion made over a hand-written list
 * would go on passing on the day a namespace is added here, which is the one day it matters.
 */
export const MERGE_FIELD_STEPS: Array<[prefix: string, step: string | null]> = [
    ['driver.', 'driver'],
    ['borrower.', 'driver'],
    // The witness's own fields — their name, their contact, how they know the hirer — are all
    // edited in the parties block, which lives on the agreements area.
    ['witness.', 'agreements'],
    ['vehicle.', 'vehicle'],
    ['contract.', 'financials'],
    ['contract.deposit_amount', 'deposit'],
    ['contract.hire_purchase_signed_date', 'agreements'],
    ['contract.hire_purchase_reference', 'agreements'],
    ['deposit.', 'deposit'],
    // THE ADVANCE DOSSIER'S OWN THREE AREAS — see the paragraph above. The prefix is the
    // disbursement, where the purpose, the rate and the transaction behind the principal are typed;
    // the two exact keys are the repayment schedule, and they beat the prefix on length.
    ['loan.', 'disbursement'],
    ['loan.weekly_instalment', 'repayment'],
    ['loan.first_instalment_date', 'repayment'],
    ['insurance.', 'insurance'],
    ['insurance_loan.', 'insurance'],
    // The partner record lives outside setup entirely, and the schedule's figures are all worked
    // out of the Financials area's own — see the paragraph above.
    ['partner.', null],
    ['schedule.', 'financials'],
];

export function stepForMergeField(field: string): string | null {
    let best: { prefix: string; step: string | null } | null = null;
    for (const [prefix, step] of MERGE_FIELD_STEPS) {
        if (!field.startsWith(prefix)) continue;
        if (!best || prefix.length > best.prefix.length) best = { prefix, step };
    }
    return best?.step ?? null;
}

/** `driver.full_name` -> `Driver — full name`, for a checklist a human reads. */
export function humanizeMergeField(field: string): string {
    const [namespace, ...rest] = field.split('.');
    const head = namespace.replace(/_/g, ' ');
    const tail = rest.join(' ').replace(/[._]/g, ' ');
    const label = `${head.charAt(0).toUpperCase()}${head.slice(1)}`;
    return tail ? `${label} — ${tail}` : label;
}

// ─── The signing link ────────────────────────────────────────────────────────

/**
 * The driver app's origin. Signing links are composed here and shared by hand — there
 * is no email in v1 — so a wrong value produces a link that visibly points at the wrong
 * host rather than a silent failure, which is why the composed URL is always shown in
 * full before it is copied.
 */
export const DRIVER_APP_URL: string =
    (import.meta.env.VITE_DRIVER_APP_URL as string | undefined)?.replace(/\/+$/, '') ||
    'https://driver.primelane.co';

/**
 * The public signing route, for BOTH parties who sign by link.
 *
 * ONE ROUTE, NOT TWO. The witness and the depositor reach the same ceremony and
 * the same token table; what differs is the wording, and the driver app derives that from
 * the party `agreement_token_lookup` reports rather than from the URL it was opened with.
 * A second path would be a second place for that decision to be made, and the two would
 * eventually disagree. (`/sign/witness` stays alive in the driver app as a redirect, so
 * links already shared before this change keep working.)
 *
 * The token travels in the FRAGMENT, never the path or the query: a fragment is not sent
 * to the server, not written to an access log and not carried in a Referer header.
 */
export function signingLink(token: string): string {
    return `${DRIVER_APP_URL}/sign/link#${token}`;
}

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * The RPCs raise `CODE: sentence` and PostgREST hands the whole string back as
 * `error.message`. Four of them describe a state the admin has to act on differently, so
 * they are surfaced distinctly rather than collapsed into "something went wrong":
 *
 *   AGREEMENT_CONTENT_CHANGED     the contract moved under a document being signed
 *   AGREEMENT_TYPED_NAME_REQUIRED the signer left the typed name blank
 *   AGREEMENT_SIGN_ORDER          somebody tried to sign out of turn
 *   AGREEMENT_CONTRACT_ACTIVE     past DRAFT: the parties are fixed while a live document names them
 *
 * The rest are mapped where a plain restatement would leave the admin guessing what to do.
 */
const ERROR_MESSAGES: Record<string, string> = {
    AGREEMENT_CONTENT_CHANGED:
        'The contract details changed since this document was rendered. Reload the document, ' +
        'review it again, and retry — the signature must attest to the text on screen.',
    AGREEMENT_TYPED_NAME_REQUIRED:
        'The signer must type their name alongside the drawn signature.',
    AGREEMENT_SIGN_ORDER:
        'Another party still has to sign first. Primelane always countersigns last.',
    /**
     * NOT SIGN_ORDER, AND THE DIFFERENCE IS THE FIX ADDRESS [D-B18]. The order gate is about a seat
     * on THIS document; this one is about a signature on ANOTHER instrument, so an admin told to
     * "wait for another party" would be waiting on the wrong page.
     *
     * AND IT IS THE ONE ENTRY THAT DOES NOT REPLACE THE SERVER'S SENTENCE [D-B27] — see
     * `DETAIL_LED_CODES`. Everything the admin needs to KNOW is in that sentence and cannot be
     * derived here: which contract, which of the two arms is outstanding (or both), and which
     * documents the rule spans — a set that grew from two to three the moment the insurance
     * premium advance joined the gate, while the copy that used to stand in its place went on
     * naming two. So this says only what the server does not: where to go.
     */
    AGREEMENT_COUNTERSIGN_BLOCKED:
        'The outstanding signature is on another document — chase it there, not on this one.',
    /**
     * ONE DOOR RAISES IT NOW, AND IT IS THE TRIGGER [D-B43].
     * `contracts_void_agreements_on_party_change` is the sole raiser: it voids the live documents
     * in scope on a DRAFT contract and raises past DRAFT while one of them still names the driver
     * or the witness being moved (it reaches this map through `useContractAgreements`).
     * `agreement_void` no longer raises it at all — every completed document is withdrawable at any
     * contract status with a stated reason, the witness agreement included. So the sentence below
     * answers for a foreign-key edit's side effect and for nothing else: the two doors were always
     * different in kind, and only the side effect still refuses.
     */
    AGREEMENT_CONTRACT_ACTIVE:
        'This contract has left DRAFT. The driver and the witness cannot be changed while a live ' +
        'document names them: void those documents first, and the change goes through. Every ' +
        'completed document is withdrawable from the agreements board with a reason, the witness ' +
        'agreement included.',
    /**
     * ONE CODE, FIVE DOORS, AND SINCE [D-B48] NO SINGLE SENTENCE CAN DESCRIBE THEM — so this one
     * LEADS WITH THE SERVER'S (see `DETAIL_LED_CODES`).
     *
     * `agreement_create` raises it when the contract names no driver, when a witness agreement's
     * contract names no witness, when a Vehicle Schedule's contract names no vehicle, when that
     * vehicle names no partner, and when the contract states no management rate or states zero —
     * five different missing facts on three different screens. The copy that stood here named the
     * driver and the witness and nothing else, which is the failure `DETAIL_LED_CODES` was created
     * for: a hand-kept restatement of a server rule that went on describing the rule it was
     * written against after the server had grown two more.
     *
     * WHAT IS ADDED IS THE ONE THING THE SERVER CANNOT SAY — that the fix is a field on this
     * contract rather than anything about the document being generated.
     */
    AGREEMENT_SUBJECT_INCOMPLETE:
        'State the missing fact on the contract and generate again.',
    // A deposit paid before any contract existed is the case this document is FOR, so the
    // refusal is about the depositor's details, never about the missing contract.
    AGREEMENT_SIGNER_REQUIRED:
        'A deposit agreement is signed by the person who paid it, and they may have no account ' +
        'yet. Enter their full name, phone number and address — the agreement prints all three.',
    AGREEMENT_SIGNER_UNEXPECTED:
        'A signer name, phone and address belong to a deposit agreement only. Every other ' +
        'document takes its signer from the contract.',
    AGREEMENT_PARTY_UNSUPPORTED:
        'That party cannot sign by link on this document. Only a witness, and the depositor on ' +
        'a deposit agreement, sign through a shared link.',
    AGREEMENT_ALREADY_SCANNED:
        'A signed paper copy is already on file for this record. Generating a document for it ' +
        'would overwrite that signature date.',
    /**
     * THREE DOORS, THREE SENTENCES, AND A FOURTH CAUSE INSIDE ONE OF THEM [D-B48] — so this one
     * LEADS WITH THE SERVER'S TOO (see `DETAIL_LED_CODES`).
     *
     * `agreement_sign` and `agreement_issue_token` raise it on the witness agreement (the driver
     * has not signed the master instrument), on the insurance premium advance (same), and now on
     * the DRIVER's own seat on the hire purchase agreement — where the vehicle belongs to a
     * third-party partner who has not executed a Vehicle Schedule, or has executed one stating a
     * fee the contract no longer charges. The copy that stood here asserted the witness rule as
     * though it were the whole code, so it misdescribed two of the three doors and both causes of
     * the third.
     *
     * THE PAPER SENTENCE CAME OFF WITH IT [D-B49]. It read "a paper original recorded on that
     * agreement counts", which was an instruction to use a route no client can take any more:
     * `signed_at`, `agreement_url` and `origin` are no longer writable by `authenticated`, and an
     * admin who followed it would meet a bare `42501`. The originals already on file still open
     * every one of these gates — that is `driverSigned`'s legacy limb, and 174 production
     * contracts depend on it — but nothing new is filed, so the copy names the door instead.
     */
    AGREEMENT_PRIOR_SIGNATURE_REQUIRED:
        'Take that signature first — the document it names is a row of its own on the agreements ' +
        'board.',
    /**
     * PRIMELANE DOES NOT STRIKE A BARGAIN WITH ITSELF [D-B48]. A Vehicle Schedule records what
     * Primelane pays a THIRD-PARTY owner for the use of their vehicle, and 259 of the 289 vehicles
     * are Primelane's own — so this is the refusal a board that mis-read the house marker would
     * walk into. `buildBoardSlots` mints no schedule slot on a house vehicle at all, which is why
     * the copy names the correction rather than the rule: the only way to reach it is a vehicle
     * that belongs to somebody else than the row says.
     */
    AGREEMENT_PARTNER_IS_HOUSE:
        'This vehicle is Primelane’s own, so there is no owner to pay and no schedule to sign. ' +
        'Check the vehicle’s partner if you expected one.',
    /**
     * THE CAR CHANGED HANDS WHILE A SCHEDULE WAS STILL OPEN ON IT [D-B48].
     *
     * `agreement_create` re-points an untouched reservation at the new owner by itself; it refuses
     * only where the open row can no longer be moved — a document already stands on it, or the new
     * owner already holds an executed schedule for this contract. Both states are cleared by the
     * same gesture, and it is one this app has a labelled control for, which is what the server
     * cannot know.
     */
    AGREEMENT_PARTNER_CHANGED:
        'Use Void & reissue on the open schedule — the replacement is raised for the partner who ' +
        'owns the vehicle now.',
    /**
     * THE CONTRACT STATES NO CASH PRICE, SO THERE IS NOTHING TO COPY [D-B59].
     *
     * `agreement_create` copies `contracts.cash_price` onto the Vehicle Schedule's record at
     * generation and refuses a NULL or non-positive one: the schedule prints it as the Cash price
     * and divides the net return, the annualised return and the recovery period by it. The board
     * holds Generate shut on the same condition, so this is the refusal a stale board walks into;
     * the copy names the area the figure is entered on.
     */
    AGREEMENT_CASH_PRICE_REQUIRED:
        'The contract states no cash price. Enter it on Financials, then generate the schedule again.',
    /**
     * THE WITHDRAWAL WOULD HAVE LEFT TWO OPEN SCHEDULES ON ONE CONTRACT [D-B48].
     *
     * `agreement_void` unseals the referent it is withdrawing, and a contract may hold only one
     * UNSIGNED schedule (`uq_cpa_contract_unsealed`) — so withdrawing a previous owner's executed
     * schedule while the current owner's is still out is refused BEFORE any write, by name. The
     * server's sentence names the contract, the partner holding the open slot and the gesture;
     * this adds the order to do it in.
     */
    AGREEMENT_PARTNER_SCHEDULE_OPEN:
        'Withdraw the open schedule first, then withdraw this one.',
    /**
     * THE EXECUTED SCHEDULE REFUSES A CORRECTION UNDERNEATH IT [D-B48] — and this one LEADS WITH
     * THE SERVER'S SENTENCE (see `DETAIL_LED_CODES`).
     *
     * `trg_contracts_void_partner_schedule` raises it on a DRAFT contract whose Vehicle Schedule is
     * sealed when `cash_price`, `sold_at`, `management_rate` or `vehicle_id` moves (the cash price
     * joined at [D-B59]). The server names the contract and states the rule — an executed
     * instrument is not edited underneath its signatures — and it reaches this app through the
     * Financials canvas, which is nowhere near the agreements board. What the server cannot know is
     * where the withdrawal is done in this app.
     *
     * ON THE MONEY BLOCK THE CLIENT USUALLY SPEAKS FIRST. `commitMoneyChange` refuses a cash-price
     * or selling-price save over an executed schedule before it voids anything, with the canvas's
     * own sentence, so this mapping is what the fee save and a stale board still meet.
     */
    PARTNER_SCHEDULE_RECORD_IMMUTABLE:
        'Use Void & reissue on the vehicle schedule, on the agreements area, before saving this.',
    AGREEMENT_NOT_FROZEN:
        'Review the document first: the witness link freezes the text the witness will attest to.',
    AGREEMENT_VOID_REASON_REQUIRED:
        'This document is complete. Voiding it requires a reason.',
    AGREEMENT_TEMPLATE_MISSING:
        'No template is seeded for this document type yet.',
    AGREEMENT_TEMPLATE_MALFORMED_FIELD:
        'This wording carries a merge field the document generator cannot read, so publishing ' +
        'it would print braces on every document it is used for. Write each one as ' +
        '{{driver.full_name}} exactly — no spaces inside the braces, every {{ closed, and no ' +
        'extra brace either side of the pair. The editor lists the offending text beside the ' +
        'markdown source.',
    AGREEMENT_NOT_COMPLETE:
        'Every party has to sign before the document can be finalized.',
    AGREEMENT_FINAL_OBJECT_MISSING:
        'The executed PDF is not in storage yet. Retry finalization.',
    AGREEMENT_FINALIZE_IN_PROGRESS:
        'Finalization is already running for this document. It will finish on its own.',
    AGREEMENT_SIGNATURE_MISSING:
        'No signature image was found for this session. Save your signature in Settings and retry.',
    AGREEMENT_SIGNATURE_PATH_INVALID:
        'The signature image is not filed against this document. Save your signature again in Settings.',
    AGREEMENT_SIGNER_UNNAMED:
        'The signer has no name on record, and a signature block cannot be printed without one.',
    /**
     * THE INSTRUMENT AND ITS SUBJECT HAVE COME APART [D-B45] — and like the countersign refusal
     * this one LEADS WITH THE SERVER'S SENTENCE (see `DETAIL_LED_CODES`).
     *
     * `agreement_create` picks between the Insurance Premium Advance and the Insurance Premium
     * Payment Record from the loan's own rows, and `agreement_sign` re-asserts that shape on both
     * arms — so a deposit that lands after a document is generated, or an instalment recorded
     * against a loan whose Record is already out, is refused at the signature rather than sealed
     * onto a page that says the wrong thing about the money. Which instrument the document is and
     * which one the loan now calls for are both in the server's sentence and neither can be
     * reconstructed here.
     *
     * IT ADDS THE DOOR, NOT THE INSTRUCTION — that is the difference from the countersign
     * precedent below. The server's sentence already ends "Withdraw this document and generate it
     * again", so a client sentence that said the same thing in its own words gave the admin two
     * instructions and two verb pairs for one action. What the server cannot know is where in this
     * app the action is: `Void & reissue` is the labelled control on the agreements board, and it
     * is one act rather than the two the server's wording implies.
     */
    AGREEMENT_INSTRUMENT_MISMATCH:
        'Use Void & reissue on the agreements board — the replacement is generated from the loan ' +
        'as it stands now.',
};

/**
 * Codes whose mapped copy FOLLOWS the server's own sentence instead of replacing it.
 *
 * Every other entry above is a REWRITE, because the RPC restates a condition where the admin needs
 * an instruction. The cross-document countersign refusal is the opposite shape: `agreement_sign`
 * raises `on contract 42, the driver has not signed the hire purchase agreement. Primelane
 * countersigns …` — the contract, the outstanding arm or arms, and the rule over every document it
 * governs, none of which this app can reconstruct from an error.
 *
 * REPLACING IT COST BOTH HALVES AT ONCE, WHICH IS WHY THIS EXISTS. The mapped copy threw away the
 * arm the server had just named, AND it was a second statement of a server rule kept by hand — so
 * when the insurance premium advance joined the gate [D-B27] the toast went on describing a
 * two-document rule that no longer existed. Leading with the sentence the server actually raised
 * cannot fall behind the server.
 *
 * THE INSTRUMENT MISMATCH IS THE SAME SHAPE [D-B45]: `agreement_sign` names which instrument the
 * document is and which one the loan's deposits and instalments now call for, and a client that
 * replaced that sentence would be telling an admin to void a document without saying why.
 *
 * THE TWO THAT JOINED AT [D-B48] ARE THE SHAPE'S OTHER HALF: not one sentence too specific to
 * throw away, but one CODE raised from too many places for any single restatement to be true of
 * all of them. `AGREEMENT_PRIOR_SIGNATURE_REQUIRED` is raised from three (the witness agreement,
 * the premium advance, and the driver's own seat held by an unsigned Vehicle Schedule) and
 * `AGREEMENT_SUBJECT_INCOMPLETE` from five (no driver, no witness, no vehicle, no partner, no
 * management rate). Each had a mapped sentence asserting ONE of its doors as the whole rule, and
 * each would have gone on asserting it. Leading with the sentence the server actually raised
 * cannot fall behind the server; the mapped half says only where to act.
 */
const DETAIL_LED_CODES: ReadonlySet<string> = new Set([
    'AGREEMENT_COUNTERSIGN_BLOCKED',
    'AGREEMENT_INSTRUMENT_MISMATCH',
    'AGREEMENT_PRIOR_SIGNATURE_REQUIRED',
    'AGREEMENT_SUBJECT_INCOMPLETE',
    // AND THE TRIGGER'S ONE [D-B48], for the same reason as the two above it: the server's sentence
    // names the contract and states the rule, and the mapped half says only where the gesture is.
    // It is raised on four watched columns now, the cash price among them [D-B59], so no single
    // restatement of which one moved could be true of every save that meets it.
    'PARTNER_SCHEDULE_RECORD_IMMUTABLE',
]);

/**
 * The unique constraint that settles the two-admins-at-once draft race.
 *
 * `agreement_template_create_draft` checks for an open draft first and refuses with a readable
 * `AGREEMENT_TEMPLATE_DRAFT_EXISTS`, but that check is not the guarantee — two clicks in the
 * same instant both pass it and both INSERT v N+1, and `(template_key, version)` is what makes
 * the second one fail. It surfaces as a raw Postgres 23505, which says "duplicate key value
 * violates unique constraint" and tells the admin nothing about what to do next.
 */
const DUPLICATE_DRAFT_CONSTRAINT = 'agreement_templates_key_version_unique';

const DUPLICATE_DRAFT_MESSAGE =
    'Somebody else just created this draft. Reload the page and edit the draft that now ' +
    'exists — a template key only ever has one open draft.';

export interface AgreementError {
    /** The `AGREEMENT_*` code, when the message carried one. */
    code: string | null;
    /** What to show the admin. */
    message: string;
}

/**
 * The `CODE: sentence` message behind a `generate-agreement` failure body.
 *
 * THE FUNCTION PUTS ITS CODE IN A FIELD, NOT IN THE SENTENCE. A render that cannot run
 * answers `{ error: "Brand fonts missing from brand-assets: …", code: "FONTS_MISSING" }`
 * with a 503, so reading `error` alone throws away the only machine-readable half — and
 * the board can then name no cause for a seal that failed. Splicing the two into the shape
 * the RPCs already raise means `parseAgreementError` reads both, and a code this app has
 * no copy for still surfaces the server's own sentence verbatim.
 */
export function composeFunctionErrorMessage(body: { error?: unknown; code?: unknown }): string {
    const message = typeof body.error === 'string' ? body.error : String(body.error ?? '');
    const code = typeof body.code === 'string' ? body.code.trim() : '';
    if (!code || message.startsWith(`${code}:`)) return message;
    return `${code}: ${message}`;
}

export function parseAgreementError(error: unknown): AgreementError {
    const raw =
        typeof error === 'string'
            ? error
            : ((error as { message?: string } | null)?.message ?? '');

    // The race arrives as a PostgREST error object, not as a `CODE: sentence` message, so it
    // is matched on the constraint name — `details` carries it when `message` does not, and a
    // bare 23505 is not enough: publishing races on a different index entirely.
    const pg = (error ?? {}) as { code?: unknown; details?: unknown };
    if (
        pg.code === '23505' &&
        `${raw} ${typeof pg.details === 'string' ? pg.details : ''}`.includes(
            DUPLICATE_DRAFT_CONSTRAINT,
        )
    ) {
        return { code: '23505', message: DUPLICATE_DRAFT_MESSAGE };
    }

    const match = /^([A-Z][A-Z0-9_]{3,}):\s*([\s\S]*)$/.exec(raw.trim());
    if (!match) {
        if (/access denied/i.test(raw)) {
            return { code: 'ACCESS_DENIED', message: 'You do not have access to do that.' };
        }
        return { code: null, message: raw || 'Something went wrong.' };
    }

    const [, code, detail] = match;
    const copy = ERROR_MESSAGES[code] as string | undefined;

    if (copy && DETAIL_LED_CODES.has(code)) {
        // The RPC always raises a sentence with this code, so the empty branch is defensive only —
        // and the instruction on its own is still true when it is taken.
        const sentence = detail?.trim() ?? '';
        if (!sentence) return { code, message: copy };
        return { code, message: `${/[.!?]$/.test(sentence) ? sentence : `${sentence}.`} ${copy}` };
    }

    return { code, message: copy ?? detail ?? raw };
}
